import { useState } from "react";
import { t } from "@lingui/macro";

import { Toast } from "widgets/feedback";
import { validationGenerator } from "base/form";
import { ADMIN_MODULE, ADMIN_ACTIONS, ADMIN_RESOURCES } from "base/constants";
import { hasPermissions } from "base/accessctrl";
import {
  getDayjsObject,
  dateTimeFormatString,
  getFormattedDate,
  formatToTimeZone
} from "base/utils";
import { useUserStore } from "base/stores";

import {
  LOGIN_ERROR_CODE_DETAILS,
  USER_STATUS,
  STRING_FIELDS,
  PERMISSION_KEY,
  DATE_FORMATS,
  EXPIRE_KEYS
} from "./constants/index.js";

export function useSingleton(
  callBack = () => {
    // callback function
  }
) {
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  if (hasBeenCalled) return;
  callBack();
  setHasBeenCalled(true);
}

export function parseQueryString(queryString) {
  var assoc = {};
  var keyValues = (queryString || window.location.search).slice(1).split("&");
  var decode = (s) => {
    return decodeURIComponent(s.replace(/\+/g, " "));
  };

  for (let keyValue of keyValues) {
    var key = keyValue.split("=");
    if (key.length > 1) {
      assoc[decode(key[0])] = decode(key[1]);
    }
  }
  return assoc;
}

export function getArgs(args) {
  if (typeof args === "string") {
    try {
      args = JSON.parse(args);
    } catch (e) {
      //throw error here
    }
  }
  return args;
}

export let helperMethods = {
  SignUp: (history) => {
    history.push("/join");
  },
  ResetPassword: (history) => {
    history.push("/forgotpassword");
  },
  OpenChat: () => {
    window.Intercom("show");
  }
};

export function initializeErrorDetails(props) {
  let {
    error: { args }
  } = props;
  let status = LOGIN_ERROR_CODE_DETAILS[props.error.error_code] && {
    ...LOGIN_ERROR_CODE_DETAILS[props.error.error_code],
    args
  };
  return status;
}

export function emailDomain(value) {
  let r = /^[a-z][a-zA-Z-0-9]*\.[a-z]+(\.[a-z]+)?$/;
  return r.test(value) ? "" : t`Invalid domain address`;
}

export function uniqueDomain(domainIds, value) {
  let isFound = domainIds.find((domain) => domain._id === value);
  if (isFound) {
    return t`Domain already exists`;
  }
  return "";
}

export function validEmail(id, value) {
  if (value && id) {
    return value.endsWith(id)
      ? ""
      : t`The email domain doesn't match with the domain above`;
  }
  return "";
}

export const makeString = (words) => {
  if (words.length === 1) return `${words[0]}`;
  const firsts = words.slice(0, words.length - 1);
  const last = words[words.length - 1];
  return `${firsts.join(", ")} and ${last}`;
};

function isCustomField(columnField, metadataModel) {
  return (
    !columnField.IsSystemField &&
    metadataModel &&
    Object.keys(metadataModel).length !== 0 &&
    !columnField.Id?.startsWith("_")
  );
}

export function constructFieldObj(
  metadataModel,
  columnsFields = [],
  handlePermissions = false
) {
  const canUpdateUserDetails = hasPermissions({
    module: ADMIN_MODULE,
    resource: ADMIN_RESOURCES.USER_MANAGEMENT,
    actions: [ADMIN_ACTIONS.UPDATE]
  });
  const canUpdateUserRole = hasPermissions({
    module: ADMIN_MODULE,
    resource: ADMIN_RESOURCES.USER_MANAGEMENT,
    actions: [ADMIN_ACTIONS.UPDATEROLE]
  });
  const FIELD_PERMISSIONS = {
    Roles: canUpdateUserRole
  };

  if (columnsFields.length !== 0) {
    for (let i in columnsFields) {
      var columnField = columnsFields[i];
      columnField["Visible"] = true;
      if (handlePermissions) {
        columnField["disableEdit"] = FIELD_PERMISSIONS.hasOwnProperty(
          columnField.Id
        )
          ? !FIELD_PERMISSIONS[columnField.Id]
          : !canUpdateUserDetails;
      }
      if (isCustomField(columnField, metadataModel)) {
        var field = metadataModel.getObject(columnField.Id);
        if (field) {
          let validationGeneratorResult = validationGenerator(field, []);
          Object.assign(columnField, field.currentJson);
          columnField.Validationfun = validationGeneratorResult[0];
          columnField.Hint = field.dataSet[field.currentJson.Column].Hint;
        }
      }
    }
  }
  return columnsFields;
}

function s4() {
  return intToBase64(Math.floor((1 + Math.random()) * 0x10000));
}

function divmod(x, y) {
  let div = Math.trunc(x / y);
  let rem = x % y;
  return [div, rem];
}

function intToBase64(num) {
  // """Converts a positive integer into a base36 string."""
  let digits =
    "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_-";
  let res = "";
  let i;
  while (!res || num > 0) {
    let result = divmod(num, 64);
    num = result[0];
    i = result[1];
    res = digits[i] + res;
  }
  return res;
}

export function generateShortId(name = "ShortId") {
  return `${name}-${s4() + intToBase64(new Date().getTime())}`;
}

export function saveToast(customTxt = t`Changes updated`) {
  return Toast.showSuccess({
    message: customTxt
  });
}

export function removeEmptyKeyFromObject(obj) {
  for (let propName in obj) {
    if (
      !obj[propName] ||
      (Array.isArray(obj[propName]) && !obj[propName]?.length)
    ) {
      Reflect.deleteProperty(obj, propName);
    }
  }
  return obj;
}

export function showPendingActions(props) {
  if (!props?.EmailVerified) {
    return true;
  }
  return isLockedUser(props);
}

export function isEmailVerified(data) {
  if (data?.EmailVerified) return true;
}

export const isInActive = (user) => {
  return user.Status === USER_STATUS.INACTIVE;
};

/**
 *
 * @param user
 * @returns if selected user is requested user then this func will return true.
 */

export const isRequestedAccess = (user) => {
  return user.Status === USER_STATUS.REQUESTED_ACCESS;
};

export function isLockedUser(value) {
  try {
    let timeDiff = null;
    let showUnlock = false;
    if (value && value.LoginAttemptAt && value.LoginAttemptAt.v) {
      timeDiff = getDayjsObject().diff(
        new Date(value.LoginAttemptAt.v),
        "minute"
      );
    }
    if (value.NoOfFailedAttempts === 3 && timeDiff !== null) {
      showUnlock = timeDiff <= 30;
    } else if (value.NoOfFailedAttempts >= 6) {
      showUnlock = true;
    }
    return showUnlock;
  } catch (err) {
    console.info("error in checking isLocked user", err);
  }
}

export function ceilDate(date) {
  date = new Date(date);
  date.setDate(date.getDate() + 1);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(theDate, days) {
  return new Date(theDate.getTime() + days * 24 * 60 * 60 * 1000);
}

export function isAccBlockedByDunning() {
  let { currentUser } = useUserStore.getState();
  if (currentUser) {
    const licenseInfo = currentUser.AccountDetails.LicenseInfo;
    if (!licenseInfo) {
      return true;
    }
    if (!licenseInfo.DunningAt) {
      return false;
    }
    const cfDunningDate = addDays(ceilDate(licenseInfo.DunningAt), 14);
    return licenseInfo.IsDunning && cfDunningDate < new Date();
  }
}

export function isDunningFailed() {
  try {
    const canAccessBilling = hasPermissions({
      module: ADMIN_MODULE,
      resource: ADMIN_RESOURCES.BILLING,
      actions: [ADMIN_ACTIONS.READ]
    });
    let { currentUser } = useUserStore.getState();
    if (currentUser) {
      const licenseInfo = currentUser.AccountDetails.LicenseInfo;
      if (
        !canAccessBilling ||
        !licenseInfo.IsDunning ||
        !licenseInfo.DunningAt
      ) {
        return false;
      }
      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      return (
        licenseInfo.IsDunning && currentDate > new Date(licenseInfo.DunningAt)
      );
    }
  } catch (error) {
    console.info("DunningFailed", error);
  }
}
export function getFileExtension(name) {
  if (name.lastIndexOf(".") === -1) {
    return "unknown";
  } else {
    let extension = name.split(".").pop();
    return extension.toLowerCase();
  }
}

export function getDataValue(key, value, fields) {
  const indexValue = fields.find(
    (column) =>
      column.Id === key &&
      !column.IsSystemField &&
      STRING_FIELDS.includes(column.Type)
  );
  if (indexValue && !(typeof value === "string" || value instanceof String)) {
    return null;
  }
  return value;
}

// NOTE: Temporarly below util added to match the a whileago and display time in reative time.
// NOTE: check with arjun to make changes in base util, since it will affect global page
export function getEpochString({ timestamp, short = false }) {
  // Epochs
  const epochs = short
    ? [
        ["y", 31536000],
        ["mo", 2592000],
        ["d", 86400],
        ["h", 3600],
        ["min", 60]
      ]
    : [
        ["year", 31536000],
        ["month", 2592000],
        ["day", 86400],
        ["hour", 3600],
        ["minute", 60]
      ];

  const singularEpochs = {
    year: t`year`,
    month: t`month`,
    day: t`day`,
    hour: t`hour`,
    minute: t`minute`,
    y: t`y`,
    mo: t`mo`,
    d: t`d`,
    h: t`h`,
    min: t`min`,
    second: t`second`
  };

  const pluralEpochs = {
    year: t`years`,
    month: t`months`,
    day: t`days`,
    hour: t`hours`,
    minute: t`minutes`,
    y: t`y`,
    mo: t`mo`,
    d: t`d`,
    h: t`h`,
    min: t`min`,
    second: t`seconds`
  };

  // Get duration
  const getDuration = (_timeAgoInSeconds) => {
    for (let [name, seconds] of epochs) {
      const _interval = Math.floor(_timeAgoInSeconds / seconds);

      if (_timeAgoInSeconds > 0 && _timeAgoInSeconds < 60) {
        return {
          interval: _timeAgoInSeconds,
          epoch: "second"
        };
      }

      if (_interval >= 1) {
        return {
          interval: _interval,
          epoch: name
        };
      }
    }
  };

  const timeAgoInSeconds = Math.floor(
    (new Date() - new Date(timestamp)) / 1000
  );

  const MAX_MONTH = 6;

  const epochDetail = getDuration(timeAgoInSeconds);

  if (!epochDetail) {
    return t`now`;
  }
  let { interval, epoch } = getDuration(timeAgoInSeconds);

  epoch = interval === 1 ? singularEpochs[epoch] : pluralEpochs[epoch];

  if (
    interval >= MAX_MONTH &&
    [singularEpochs.month, pluralEpochs.month].includes(epoch)
  ) {
    return t`A while ago`;
  }

  let ago = t`ago`;

  return short ? `${interval} ${epoch}` : `${interval} ${epoch} ${ago}`;
}

export const checkPermission = (Key, FieldData) => {
  if (Key === PERMISSION_KEY.IsHidden && !FieldData) {
    return true;
  }
  return FieldData && FieldData[Key];
};

export const getDateFormat = () => {
  const [localFormat] = DATE_FORMATS;
  let currentUser = window.accountDetails.UserDetails;
  let { AccountDetails } = currentUser;
  let localeDateFormat =
    currentUser.DateFormat === localFormat._id
      ? AccountDetails.DateFormat
      : currentUser.DateFormat;

  return dateTimeFormatString(localeDateFormat);
};

export const getDifferenceDays = (date) => {
  let diff = getDayjsObject()
    .endOf("day")
    .diff(getDayjsObject(date).endOf("day"), "day");
  return Math.abs(diff);
};

export const getExpireDate = (expiryDays = {}, customExpiryDate) => {
  let date = new Date();
  if (expiryDays.key === EXPIRE_KEYS.SIX_MONTH) {
    date = getDayjsObject().add(6, "month");
  } else if (expiryDays.key === EXPIRE_KEYS.ONE_YEAR) {
    date = getDayjsObject().add(1, "year");
  } else if (expiryDays.key === EXPIRE_KEYS.TWO_YEAR) {
    date = getDayjsObject().add(2, "year");
  } else if (expiryDays.key === EXPIRE_KEYS.CUSTOM_DATES) {
    date = getDayjsObject(customExpiryDate);
  }
  return getFormattedDate(date, "D MMM YYYY");
};

/**
 * Function to sort array of objects (Objects without "isDelete" come first)
 * @param {object[]} arr
 * @returns {object[]}
 */
export function sortByIsDelete(arr) {
  arr.sort((a, b) => {
    if (!a.IsDelete && b.IsDelete) {
      return -1;
    } else if (a.IsDelete && !b.IsDelete) {
      return 1;
    }

    return 0;
  });

  return arr;
}

export const isExpired = (isDateExpired = "") => {
  if (!isDateExpired) return false;
  return getDayjsObject()
    .endOf("day")
    .isSameOrAfter(getDayjsObject(isDateExpired));
};

export const getActiveDays = (item = {}, isExpired) => {
  const { _created_at: createdAt, ExpiredAt } = item;
  const diff = getDayjsObject(createdAt)
    .startOf("day")
    .diff(getDayjsObject(isExpired ? ExpiredAt.dv : "").startOf("day"), "day");
  return Math.abs(diff) + 1;
};

export const excludeDuplicates = (array1, array2) => {
  /** extracting id's from array2 */
  let result = new Set(array2.map(({ _id }) => _id));
  const uniqueItems = array1.filter((newObj) => {
    /** excluding duplicates using has method */
    return !result.has(newObj._id);
  });
  /** concatinating both uniqueitem array and original array */
  return array2.concat(uniqueItems);
};

export function getDateFormatByTimezone(timezone) {
  return DATE_FORMATS.map((format) => {
    if (format._id === "LOCAL") return format;

    const currentDateFormat = formatToTimeZone(
      new Date().toISOString(),
      format._id,
      timezone._id
    );

    return {
      _id: format._id,
      value: `${format._id} (${currentDateFormat})`
    };
  });
}
